import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DocumentTemplate,
  DocumentVisibility,
  DocumentStatus,
  type DocumentInput,
  type PublicDocument,
} from "@vedaunion/shared";
import { BlockEditor } from "../../components/editor/BlockEditor";
import { documentEditorConfig } from "../../components/editor/documentConfig";
import {
  ensureDoc,
  emptyDoc,
  toHtml as docToHtml,
  toExcerpt,
} from "../../components/editor/utils";
import type { BlockDoc } from "../../components/editor/types";
import { DOCUMENT_TEMPLATES, findDocumentTemplate } from "../../components/document/templates";
import { TagPicker } from "../../components/document/TagPicker";
import { DocumentReferencePicker } from "../../components/document/DocumentReferencePicker";
import { AttachmentManager } from "../../components/document/AttachmentManager";
import {
  useDocument,
  useCreateDocument,
  useUpdateDocument,
  useDeleteDocument,
} from "../../lib/documents";
import { useCan, useMe } from "../../lib/auth";
import { ApiError } from "../../lib/api";

/**
 * Document create / edit page. Mirrors the article editor — same
 * BlockEditor, same sidebar pattern — with three additions specific to
 * documents: tag picker, attachment manager, and "see also" picker.
 *
 * The attachment manager is gated on `mode === "edit"` so authors save
 * the metadata first, then upload PDFs / wire YouTube refs against a real
 * document id. This sidesteps a chicken-and-egg around media uploads.
 */

const STATUS_OPTIONS = DocumentStatus.options;
const VIS_OPTIONS = DocumentVisibility.options;
const TPL_OPTIONS = DocumentTemplate.options;

interface MetaState {
  title: string;
  subtitle: string;
  excerpt: string;
  coverImage: string;
  templateKey: typeof TPL_OPTIONS[number];
  status: typeof STATUS_OPTIONS[number];
  visibility: typeof VIS_OPTIONS[number];
  pinned: boolean;
  tagIds: number[];
  relatedDocumentIds: number[];
  revisionNote: string;
  publishedAt: string;
}

const DEFAULT_META: MetaState = {
  title: "",
  subtitle: "",
  excerpt: "",
  coverImage: "",
  templateKey: "blank",
  status: "draft",
  visibility: "public",
  pinned: false,
  tagIds: [],
  relatedDocumentIds: [],
  revisionNote: "",
  publishedAt: "",
};

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function DocumentForm({ mode }: { mode: "create" | "edit" }) {
  const navigate = useNavigate();
  const params = useParams<{ slug: string }>();
  const me = useMe();
  const canPublish = useCan("documents.publish");
  const canPin = useCan("documents.pin");
  const canEditAny = useCan("documents.edit_any");
  const canDelete = useCan("documents.delete_any");

  const docQ = useDocument(mode === "edit" ? params.slug : undefined);
  const document = docQ.data?.document ?? null;
  const create = useCreateDocument();
  const update = useUpdateDocument(document?.id ?? 0);
  const remove = useDeleteDocument();

  const hasInteractedRef = useRef(false);
  const [meta, setMetaRaw] = useState<MetaState>(DEFAULT_META);
  const [doc, setDocRaw] = useState<BlockDoc>(() => emptyDoc());
  const setMeta = (updater: MetaState | ((prev: MetaState) => MetaState)) => {
    hasInteractedRef.current = true;
    setMetaRaw(updater);
  };
  const setDoc = (next: BlockDoc) => {
    hasInteractedRef.current = true;
    setDocRaw(next);
  };
  const [error, setError] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(mode === "create");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (mode !== "edit" || !document) return;
    setMetaRaw({
      title: document.title,
      subtitle: document.subtitle ?? "",
      excerpt: document.excerpt ?? "",
      coverImage: document.coverImage ?? "",
      templateKey: document.templateKey,
      status: document.status,
      visibility: document.visibility,
      pinned: !!document.pinnedAt,
      tagIds: document.tags.map((t) => t.id),
      relatedDocumentIds: document.relatedDocuments.map((d) => d.id),
      revisionNote: "",
      publishedAt: toDatetimeLocalValue(document.publishedAt),
    });
    setDocRaw(ensureDoc(document.contentJson));
    setShowTemplatePicker(false);
    hasInteractedRef.current = false;
  }, [mode, document]);

  const isOwn = !!me.data?.user && document?.authorId === me.data.user.id;
  const canEditThis = mode === "create" || isOwn || canEditAny;

  const allowedStatuses = useMemo(() => {
    const set = new Set<typeof STATUS_OPTIONS[number]>(["draft", "review", "archived"]);
    if (canPublish || document?.status === "published") set.add("published");
    return STATUS_OPTIONS.filter((s) => set.has(s));
  }, [canPublish, document?.status]);

  const onPickTemplate = (key: typeof TPL_OPTIONS[number]) => {
    const tpl = findDocumentTemplate(key);
    setMeta((m) => ({
      ...m,
      templateKey: key,
      title: m.title || (key === "blank" ? "" : tpl.label),
    }));
    setDoc(structuredClone(tpl.doc));
    setShowTemplatePicker(false);
  };

  const buildPayload = (): DocumentInput => {
    const html = docToHtml(doc, documentEditorConfig);
    const autoExcerpt = meta.excerpt.trim() || toExcerpt(doc, documentEditorConfig);
    return {
      title: meta.title.trim(),
      subtitle: meta.subtitle.trim() || null,
      excerpt: autoExcerpt || null,
      coverImage: meta.coverImage.trim() || null,
      contentJson: doc,
      contentHtml: html,
      templateKey: meta.templateKey,
      status: meta.status,
      visibility: meta.visibility,
      pinnedAt: meta.pinned ? new Date().toISOString() : null,
      tagIds: meta.tagIds,
      relatedDocumentIds: meta.relatedDocumentIds,
      revisionNote: meta.revisionNote.trim() || null,
      ...(canPublish && meta.publishedAt
        ? { publishedAt: fromDatetimeLocalValue(meta.publishedAt) }
        : {}),
    };
  };

  const onSave = async () => {
    setError(null);
    if (!meta.title.trim()) {
      setError("Title is required.");
      return;
    }
    const payload = buildPayload();
    try {
      let saved: { document: PublicDocument };
      if (mode === "create") {
        saved = await create.mutateAsync(payload);
      } else if (document) {
        saved = await update.mutateAsync(payload);
      } else {
        return;
      }
      setSavedAt(new Date());
      navigate(`/dashboard/documents/${saved.document.slug}/edit`);
    } catch (err) {
      const e = err as ApiError;
      setError(e?.body?.message ?? e?.message ?? "Save failed.");
    }
  };

  // Auto-save for drafts only.
  useEffect(() => {
    if (mode !== "edit" || !document) return;
    if (document.status !== "draft" || meta.status !== "draft") return;
    if (!canEditThis) return;
    if (!hasInteractedRef.current) return;
    const timer = window.setTimeout(async () => {
      try {
        const html = docToHtml(doc, documentEditorConfig);
        await update.mutateAsync({
          title: meta.title.trim() || document.title,
          subtitle: meta.subtitle.trim() || null,
          excerpt: meta.excerpt.trim() || null,
          coverImage: meta.coverImage.trim() || null,
          contentJson: doc,
          contentHtml: html,
          templateKey: meta.templateKey,
          visibility: meta.visibility,
          pinnedAt: meta.pinned ? new Date().toISOString() : null,
          tagIds: meta.tagIds,
          relatedDocumentIds: meta.relatedDocumentIds,
        });
        setSavedAt(new Date());
        hasInteractedRef.current = false;
      } catch {
        /* swallow — explicit Save surfaces failures */
      }
    }, 4000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doc,
    meta.title,
    meta.subtitle,
    meta.excerpt,
    meta.coverImage,
    meta.tagIds,
    meta.relatedDocumentIds,
    meta.visibility,
    meta.templateKey,
    meta.status,
    meta.pinned,
    mode,
    document?.id,
  ]);

  const onPreview = () => {
    if (mode === "create" || !document) return;
    window.open(`/library/${document.slug}?preview=1`, "_blank", "noopener");
  };

  const onDelete = async () => {
    if (!document) return;
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await remove.mutateAsync(document.id);
      navigate("/dashboard/documents");
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Delete failed.");
    }
  };

  if (mode === "edit" && docQ.isLoading) return <p className="container-edit">Loading…</p>;
  if (mode === "edit" && !document) return <p className="container-edit">Document not found.</p>;
  if (mode === "edit" && !canEditThis)
    return <p className="container-edit">You don't have permission to edit this document.</p>;

  const savedAgo = savedAt ? Math.max(1, Math.round((Date.now() - savedAt.getTime()) / 1000)) : null;

  return (
    <div className="container-edit max-w-[1280px]">
      <header className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="page-eyebrow">{mode === "create" ? "New document" : "Edit document"}</p>
          <h1 className="page-title">{mode === "create" ? "Begin a new document." : document?.title}</h1>
        </div>
        <div className="hidden md:flex flex-wrap gap-3 items-center">
          {savedAgo !== null && (
            <span className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
              Saved {savedAgo < 60 ? `${savedAgo}s ago` : `${Math.round(savedAgo / 60)}m ago`}
            </span>
          )}
          <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
          {mode === "edit" && (
            <button type="button" className="btn-app-ghost" onClick={onPreview}>
              Preview
            </button>
          )}
          {mode === "edit" && canDelete && (
            <button type="button" className="btn-app-ghost is-danger" onClick={() => void onDelete()}>
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn-app"
            onClick={() => void onSave()}
            disabled={create.isPending || update.isPending}
          >
            {create.isPending || update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {error && <p className="auth-error mb-4">— {error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
        <div className="flex flex-col gap-4">
          {showTemplatePicker ? (
            <TemplatePicker onPick={onPickTemplate} />
          ) : (
            <>
              <input
                className="auth-input"
                style={{ fontSize: "1.5rem", padding: "1rem 1.1rem", fontFamily: "var(--font-display)" }}
                placeholder="Title"
                value={meta.title}
                onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
              />
              <input
                className="auth-input"
                placeholder="Optional subtitle (e.g. lineage, source)"
                value={meta.subtitle}
                onChange={(e) => setMeta((m) => ({ ...m, subtitle: e.target.value }))}
              />
              <BlockEditor
                doc={doc}
                onChange={setDoc}
                config={documentEditorConfig}
                editable={canEditThis}
                placeholder="A document is a metadata page — a short overview that frames the resources. Keep it brief."
              />

              {/* Resources panel — admin */}
              {mode === "edit" && document && (
                <section className="veda-panel mt-8">
                  <header className="mb-4">
                    <p className="page-eyebrow mb-1">Resources</p>
                    <h2 className="font-display text-[1.4rem] leading-[1.2] tracking-tight">
                      Materials attached to this document
                    </h2>
                    <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-1.5 max-w-[60ch]">
                      The resources rail on the public page is built from these. Reorder
                      with the arrows; readers see them in this order.
                    </p>
                  </header>
                  <AttachmentManager documentId={document.id} />
                </section>
              )}
            </>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          <Field label="Status">
            <select
              className="auth-input"
              value={meta.status}
              onChange={(e) =>
                setMeta((m) => ({ ...m, status: e.target.value as MetaState["status"] }))
              }
            >
              {allowedStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          {canPublish && (
            <Field
              label="Publish date"
              hint="Leave blank to use today's date when published."
            >
              <input
                className="auth-input"
                type="datetime-local"
                value={meta.publishedAt}
                onChange={(e) => setMeta((m) => ({ ...m, publishedAt: e.target.value }))}
              />
            </Field>
          )}

          <Field label="Visibility">
            <select
              className="auth-input"
              value={meta.visibility}
              onChange={(e) =>
                setMeta((m) => ({ ...m, visibility: e.target.value as MetaState["visibility"] }))
              }
            >
              {VIS_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>

          {canPin && (
            <Field
              label="Pin to Library"
              hint="Pinned documents float at the top of the Library Featured tab."
            >
              <label
                className="auth-input flex items-center gap-3 cursor-pointer"
                style={{ background: "var(--color-vellum-dim)" }}
              >
                <input
                  type="checkbox"
                  checked={meta.pinned}
                  onChange={(e) => setMeta((m) => ({ ...m, pinned: e.target.checked }))}
                  style={{ width: "auto" }}
                />
                <span className="font-grotesk text-[0.85rem]">{meta.pinned ? "Pinned" : "Off"}</span>
              </label>
            </Field>
          )}

          <Field label="Template">
            <select
              className="auth-input"
              value={meta.templateKey}
              onChange={(e) =>
                setMeta((m) => ({ ...m, templateKey: e.target.value as MetaState["templateKey"] }))
              }
            >
              {TPL_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Cover image URL" hint="Shown in cards and as the public page header.">
            <input
              className="auth-input"
              type="url"
              placeholder="https://"
              value={meta.coverImage}
              onChange={(e) => setMeta((m) => ({ ...m, coverImage: e.target.value }))}
            />
          </Field>

          <Field label="Excerpt" hint="Shown in cards and search results.">
            <textarea
              className="auth-input"
              rows={3}
              value={meta.excerpt}
              onChange={(e) => setMeta((m) => ({ ...m, excerpt: e.target.value }))}
            />
          </Field>

          <Field label="Tags">
            <TagPicker
              selectedIds={meta.tagIds}
              onChange={(next) => setMeta((m) => ({ ...m, tagIds: next }))}
            />
          </Field>

          <Field label="See also">
            <DocumentReferencePicker
              selectedIds={meta.relatedDocumentIds}
              excludeIds={document ? [document.id] : []}
              onChange={(next) => setMeta((m) => ({ ...m, relatedDocumentIds: next }))}
              hint="Linked both ways automatically — no need to add the reverse."
            />
          </Field>

          {mode === "edit" && (
            <Field label="Revision note" hint="Saved with this version.">
              <input
                className="auth-input"
                type="text"
                placeholder="Tightened the introduction"
                value={meta.revisionNote}
                onChange={(e) => setMeta((m) => ({ ...m, revisionNote: e.target.value }))}
              />
            </Field>
          )}

          {mode === "create" && (
            <button
              type="button"
              className="btn-app-ghost"
              onClick={() => setShowTemplatePicker(true)}
            >
              Pick a different template
            </button>
          )}
        </aside>
      </div>

      {/* Mobile sticky action bar */}
      <div className="md:hidden form-action-bar mt-8">
        <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
          Cancel
        </button>
        {mode === "edit" && (
          <button type="button" className="btn-app-ghost" onClick={onPreview}>
            Preview
          </button>
        )}
        <button
          type="button"
          className="btn-app"
          onClick={() => void onSave()}
          disabled={create.isPending || update.isPending}
        >
          {create.isPending || update.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="auth-field">
      <label className="auth-label">{label}</label>
      {children}
      {hint && <p className="auth-hint">{hint}</p>}
    </div>
  );
}

function TemplatePicker({ onPick }: { onPick: (key: typeof TPL_OPTIONS[number]) => void }) {
  return (
    <div>
      <p className="eyebrow mb-3">Choose a template</p>
      <h2 className="font-display text-[clamp(1.5rem,3.5vw,2.1rem)] tracking-tight mb-3">
        A pre-arranged starting page.
      </h2>
      <p className="text-[1rem] text-[var(--color-ink-soft)] mb-9 max-w-[60ch]">
        Documents are resource pages — these templates frame the introduction
        that surrounds the materials. The resources panel itself is filled
        in once you've saved.
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <li>
          <button
            type="button"
            onClick={() => onPick("blank")}
            className="template-card template-card-blank"
          >
            <p className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-2">
              Blank
            </p>
            <p className="font-display text-[1.5rem] leading-[1.15] tracking-tight mb-3">
              Start from nothing.
            </p>
            <p className="text-[0.95rem] leading-[1.5] text-[var(--color-ink-soft)]">
              An empty page. Add blocks as you go.
            </p>
          </button>
        </li>
        {DOCUMENT_TEMPLATES.map((t) => (
          <li key={t.key}>
            <button type="button" onClick={() => onPick(t.key)} className="template-card">
              <div
                className="template-card-preview"
                dangerouslySetInnerHTML={{ __html: t.previewHtml }}
              />
              <div className="template-card-meta">
                <p className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1.5">
                  {t.label}
                </p>
                <p className="text-[0.95rem] leading-[1.5] text-[var(--color-ink-soft)]">
                  {t.blurb}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
